import { SealCheck } from "@phosphor-icons/react";
import { parseCoverImageUrl } from "@replicate/ui";
import { useMemo } from "react";
import { match } from "ts-pattern";
import { getFallbackCoverImage } from "../utils";
import { VideoOutput } from "../video-output";

export interface ModelExample {
  name: string;
  owner: string;
  coverImage: string | undefined | null;
  runCount: number;
  // This is the humanized run count, e.g., "11.5k"
  runCountDisplay: string;
  description: string | undefined | null;
  url: string;
  isOfficial?: boolean;
  avatarImage?: string | undefined | null;
}

interface ModelCardProps {
  model: ModelExample;
  loading?: HTMLImageElement["loading"];
}

function Fallback({ seed }: { seed?: string }) {
  const fallbackCoverImage = useMemo(() => getFallbackCoverImage(seed), [seed]);
  return (
    <div className="size-full relative">
      <img
        src={fallbackCoverImage}
        alt="Fallback cover"
        className="w-full h-full object-cover absolute inset-0 bg-cover bg-center"
      />
    </div>
  );
}

function ModelCardImage({
  src,
  alt,
  loading,
  seed,
}: {
  src: string | null | undefined;
  alt: string;
  loading: HTMLImageElement["loading"];
  seed?: string;
}) {
  const parsed = parseCoverImageUrl({ url: src });

  if (!parsed) {
    return <Fallback seed={seed} />;
  }

  return match(parsed)
    .with({ fileType: "image" }, ({ url }) => (
      <img
        loading={loading}
        className="w-full h-full object-cover"
        alt={alt}
        src={url}
      />
    ))
    .with({ fileType: "video" }, ({ url }) => (
      <VideoOutput
        options={{
          controls: false,
          loop: true,
          playing: true,
          showPlayIcon: true,
        }}
        url={url}
      />
    ))
    .exhaustive();
}

// Small horizontal card (matching model_card_small.html template)
export function SmallModelCard({ model, loading = "lazy" }: ModelCardProps) {
  return (
    <div className="no-default flex flex-row group border border-r8-gray-12 hover:border-r8-gray-12 hover:bg-r8-gray-3 group-focus:border-r8-gray-12 group-focus:bg-r8-gray-12 group-focus:ring ring-r8-gray-12 h-full">
      <div className="w-20 h-20 flex-shrink-0 bg-r8-gray-3 overflow-hidden border-r border-r8-gray-12">
        <ModelCardImage
          loading={loading}
          src={model.coverImage}
          alt={model.name}
          seed={`${model.owner}/${model.name}`}
        />
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="p-2.5 h-full flex flex-col justify-between">
          <h4
            className="inline-block text-r8-sm group-focus:bg-r8-gray-12 truncate max-w-full overflow-hidden leading-tight"
            translate="no"
          >
            <span className="text-r8-gray-11 group-focus:text-r8-gray-1 whitespace-nowrap">
              {model.owner}
            </span>
            <span className="text-r8-gray-11 group-focus:text-r8-gray-1 whitespace-nowrap">
              /
            </span>
            <span className="font-semibold text-r8-gray-12 group-focus:text-r8-gray-1 whitespace-nowrap">
              {model.name}
            </span>
          </h4>
          <p className="text-r8-xs text-r8-gray-11 group-focus:text-r8-gray-1 truncate whitespace-nowrap overflow-hidden">
            {model.description || ""}
          </p>
          <div className="flex items-center justify-between gap-2">
            <div className="text-r8-xs text-branding-red whitespace-nowrap">
              {model.runCountDisplay} {model.runCount === 1 ? "run" : "runs"}
            </div>
            {model.isOfficial ? (
              <div className="text-r8-xs text-r8-gray-11 group-focus:text-r8-gray-8 flex items-center gap-1">
                <SealCheck />
                Official
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
